/**
 * SimulationContext — System 访问状态、RNG、ID、事件与定义的受控入口。
 * System 只能通过此 API 操作，禁止全局单例。
 */

import type {
  CommandId,
  DomainEvent,
  EntityId,
  EventRecordId,
} from '@crater-zero/shared-types';

import { IdAllocator } from './id-allocator.js';
import { SeededRng } from './rng.js';
import type { WorldState } from './world-state.js';

/** 由 System 提交、待写入当前 Tick 事件缓冲的事件（不含 eventId/tick/sequence，由内核分配）。 */
export interface PendingEvent<TPayload = unknown> {
  readonly type: string;
  readonly payload: TPayload;
  readonly causalCommandId?: CommandId;
}

export class SimulationContext {
  /** 当前 Tick（只读；由时钟系统推进）。 */
  readonly tick: number;
  readonly rng: SeededRng;
  readonly ids: IdAllocator;

  /** 本 Tick 收集的事件（内核在 Tick 末尾统一编号并落缓冲）。 */
  private readonly pending: PendingEvent[] = [];

  constructor(
    /** 可变世界状态引用 — System 通过 ctx.state 修改权威状态。 */
    readonly state: WorldState,
    tick: number,
    rng: SeededRng,
    ids: IdAllocator,
  ) {
    this.tick = tick;
    this.rng = rng;
    this.ids = ids;
  }

  /** 提交一个领域事件到当前 Tick。 */
  emit<TPayload>(event: PendingEvent<TPayload>): void {
    this.pending.push(event as PendingEvent);
  }

  /** 当前待编号事件数量（内核用于把事件归属到触发它的命令）。 */
  get pendingCount(): number {
    return this.pending.length;
  }

  /** 内核用：取出并清空本 Tick 的待编号事件。 */
  drainPending(): readonly PendingEvent[] {
    const out = this.pending.slice();
    this.pending.length = 0;
    return out;
  }

  /** 分配实体 ID（便捷代理）。 */
  allocEntity(): EntityId {
    return this.ids.allocEntity();
  }

  /** 分配事件记录 ID（内核用）。 */
  allocEventRecord(): EventRecordId {
    return this.ids.allocEventRecord();
  }
}

/** 完整编号的 Domain Event，写入 Tick 缓冲。 */
export type RecordedEvent = DomainEvent<string, unknown>;
